<template>
<div>
    <h1>Alertes</h1>

    <GridLoader v-if="loading" class="loader"></GridLoader>

    <div v-if="!loading" class="alertsSummaryGrid">
      <div class="alertsSummaryCard alertsSummaryDanger">
        <div class="alertsSummaryLabel">Rendez-vous hors ouverture</div>
        <div class="alertsSummaryValue">{{ alertItems.length }}</div>
      </div>
      <div class="alertsSummaryCard alertsSummaryDangerSoft">
        <div class="alertsSummaryLabel">Sans période active</div>
        <div class="alertsSummaryValue">{{ noPlanningPeriodCount }}</div>
      </div>
      <div class="alertsSummaryCard alertsSummaryWarning">
        <div class="alertsSummaryLabel">Fusions clients potentielles</div>
        <div class="alertsSummaryValue">{{ mergeCandidates.length }}</div>
      </div>
    </div>

    <!-- Planning coverage -->
    <section v-if="!loading" class="alertsSection">
      <div class="alertsSectionTitleRow">
        <h2>Couverture planning</h2>
        <span class="alertsSectionCount">{{ alertItems.length }}</span>
      </div>
      <p class="alertsSectionSubtitle">Rendez-vous futurs qui ne correspondent plus à une ouverture disponible.</p>

      <div v-if="alertItems.length === 0" class="alertsEmptyState">
        Aucun rendez-vous problématique.
      </div>

      <DataTable v-if="alertItems.length > 0" :value="alertItems">
        <DataTableColumn field="name" header="Client"></DataTableColumn>
        <DataTableColumn field="start" header="Date">
          <template #body="{data}">{{ getNiceDate(data.start) }} - {{ getNiceTime(data.start) }}</template>
        </DataTableColumn>
        <DataTableColumn field="duration" header="Durée">
          <template #body="{data}">{{ writeMinutes(data.duration) }}</template>
        </DataTableColumn>
        <DataTableColumn field="issue" header="Problème">
          <template #body="{data}">
            <span class="alertsIssueBadge" :class="data.issue === 'noPlanningPeriod' ? 'alertsIssueCritical' : 'alertsIssueWarning'">
              {{ issueLabel(data.issue) }}
            </span>
          </template>
        </DataTableColumn>
        <DataTableColumn field="actions" header="">
          <template #body="{data}">
            <div class="alertsTableActions">
              <InputButton icon="pi pi-calendar" label="Agenda" class="p-button-text p-button-sm" @click="goToAgenda(data.start)"/>
              <InputButton v-if="data.clientid" icon="pi pi-user" label="Client" class="p-button-text p-button-sm" @click="goToClient(data.clientid)"/>
            </div>
          </template>
        </DataTableColumn>
      </DataTable>
    </section>

    <!-- Merge candidates -->
    <section v-if="!loading" class="alertsSection">
      <div class="alertsSectionTitleRow">
        <h2>Doublons clients potentiels</h2>
        <span class="alertsSectionCount">{{ mergeCandidates.length }}</span>
      </div>
      <p class="alertsSectionSubtitle">Détection de doublons possibles sur le téléphone ou le nom. Comparez et fusionnez directement.</p>

      <div v-if="mergeCandidates.length === 0" class="alertsEmptyState">
        Aucune fusion potentielle détectée.
      </div>

      <div class="mergeCandidatesList">
        <div v-for="(pair, idx) in mergeCandidates" :key="idx" class="mergePairCard">
          <div class="mergePairReason">
            <span class="mergePairReasonBadge">{{ mergeReasonLabel(pair.reason) }}</span>
          </div>

          <div class="mergePairColumns">
            <div class="mergePairClient" :class="{ mergePairSelected: pair._keepChoice === 'left' }">
              <div class="mergePairClientHeader">
                <strong>{{ pair.leftName }}</strong>
              </div>
              <div class="mergePairField"><span class="mergePairLabel">Téléphone</span><span>{{ pair.leftPhone || '-' }}</span></div>
              <div class="mergePairField"><span class="mergePairLabel">Adresse</span><span>{{ pair.leftAddress || '-' }}</span></div>
              <div class="mergePairField" v-if="pair.leftComment"><span class="mergePairLabel">Commentaire</span><span>{{ pair.leftComment }}</span></div>
              <div class="mergePairStats">
                <span class="mergePairStat"><i class="pi pi-history"></i> {{ pair.leftPast }} passés</span>
                <span class="mergePairStat"><i class="pi pi-calendar"></i> {{ pair.leftFuture }} futurs</span>
              </div>
              <InputButton
                label="Conserver celui-ci"
                icon="pi pi-check"
                class="p-button-outlined p-button-sm mergePairKeepBtn"
                :class="{ 'p-button-success': pair._keepChoice !== 'left', 'p-button-secondary': pair._keepChoice === 'left' }"
                :disabled="pair._merging"
                @click="executeMerge(pair, 'left')"
              />
            </div>

            <div class="mergePairVs">VS</div>

            <div class="mergePairClient" :class="{ mergePairSelected: pair._keepChoice === 'right' }">
              <div class="mergePairClientHeader">
                <strong>{{ pair.rightName }}</strong>
              </div>
              <div class="mergePairField"><span class="mergePairLabel">Téléphone</span><span>{{ pair.rightPhone || '-' }}</span></div>
              <div class="mergePairField"><span class="mergePairLabel">Adresse</span><span>{{ pair.rightAddress || '-' }}</span></div>
              <div class="mergePairField" v-if="pair.rightComment"><span class="mergePairLabel">Commentaire</span><span>{{ pair.rightComment }}</span></div>
              <div class="mergePairStats">
                <span class="mergePairStat"><i class="pi pi-history"></i> {{ pair.rightPast }} passés</span>
                <span class="mergePairStat"><i class="pi pi-calendar"></i> {{ pair.rightFuture }} futurs</span>
              </div>
              <InputButton
                label="Conserver celui-ci"
                icon="pi pi-check"
                class="p-button-outlined p-button-sm mergePairKeepBtn"
                :class="{ 'p-button-success': pair._keepChoice !== 'right', 'p-button-secondary': pair._keepChoice === 'right' }"
                :disabled="pair._merging"
                @click="executeMerge(pair, 'right')"
              />
            </div>
          </div>

          <div v-if="pair._merging" class="mergePairMerging">
            <i class="pi pi-spin pi-spinner"></i> Fusion en cours...
          </div>
          <div v-if="pair._merged" class="mergePairMerged">
            <i class="pi pi-check-circle"></i> Fusionné avec succès
          </div>
        </div>
      </div>
    </section>
</div>
</template>

<style scoped>
.alertsSummaryGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.alertsSummaryCard {
  border-radius: 10px;
  padding: 12px;
  border: 1px solid #e1e8f5;
  background: #ffffff;
}

.alertsSummaryDanger {
  border-color: #f1c8c8;
  background: #fff5f5;
}

.alertsSummaryDangerSoft {
  border-color: #f0d9bf;
  background: #fff8ee;
}

.alertsSummaryWarning {
  border-color: #eddcb2;
  background: #fffbef;
}

.alertsSummaryLabel {
  font-size: 0.84rem;
  color: #546482;
  margin-bottom: 3px;
}

.alertsSummaryValue {
  font-size: 1.45rem;
  font-weight: 800;
  color: #253756;
}

.alertsSection {
  border: 1px solid #e3eaf6;
  border-radius: 12px;
  background: #ffffff;
  padding: 14px;
  margin-bottom: 14px;
}

.alertsSectionTitleRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.alertsSectionTitleRow h2 {
  margin: 0;
  font-size: 1.07rem;
}

.alertsSectionCount {
  border: 1px solid #d6e0f0;
  color: #4a5b7b;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
}

.alertsSectionSubtitle {
  margin: 0 0 10px 0;
  color: #62718c;
  font-size: 0.9rem;
}

.alertsEmptyState {
  color: #61718d;
  border: 1px dashed #d9e3f2;
  border-radius: 10px;
  padding: 12px;
  background: #fbfdff;
}

.alertsIssueBadge {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 0.78rem;
  font-weight: 700;
}

.alertsIssueCritical {
  background: #ffe7e7;
  color: #8a1f1f;
}

.alertsIssueWarning {
  background: #fff2df;
  color: #87500b;
}

.alertsTableActions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

/* Merge candidates */
.mergeCandidatesList {
  display: grid;
  gap: 12px;
}

.mergePairCard {
  border: 1px solid #e2e9f5;
  border-radius: 12px;
  background: #fafcff;
  padding: 14px;
}

.mergePairReason {
  margin-bottom: 10px;
}

.mergePairReasonBadge {
  display: inline-block;
  background: #fff2df;
  color: #87500b;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 700;
}

.mergePairColumns {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: start;
}

.mergePairVs {
  display: flex;
  align-items: center;
  padding-top: 1.5rem;
  font-weight: 800;
  color: #94a3b8;
  font-size: 0.85rem;
}

.mergePairClient {
  border: 2px solid #e2e9f5;
  border-radius: 10px;
  padding: 10px 12px;
  background: #ffffff;
  transition: border-color 0.15s;
}

.mergePairSelected {
  border-color: #86efac;
  background: #f0fdf4;
}

.mergePairClientHeader {
  margin-bottom: 8px;
  font-size: 1rem;
}

.mergePairField {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.88rem;
  border-top: 1px dashed #edf1f6;
}

.mergePairLabel {
  color: #64748b;
  flex-shrink: 0;
}

.mergePairStats {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8edf5;
}

.mergePairStat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  color: #475569;
  font-weight: 600;
}

.mergePairStat i {
  font-size: 0.85rem;
  color: #94a3b8;
}

.mergePairKeepBtn {
  width: 100%;
  margin-top: 10px;
}

.mergePairMerging,
.mergePairMerged {
  text-align: center;
  margin-top: 10px;
  padding: 8px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
}

.mergePairMerging {
  background: #eff6ff;
  color: #1d4ed8;
}

.mergePairMerged {
  background: #f0fdf4;
  color: #15803d;
}

@media (max-width: 900px) {
  .alertsSummaryGrid {
    grid-template-columns: 1fr;
  }

  .mergePairColumns {
    grid-template-columns: 1fr;
  }

  .mergePairVs {
    justify-content: center;
    padding: 0;
  }
}
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import { authHeader } from '../helpers/authHeader.js';
import { getNiceDate, getNiceTime, writeMinutes } from '../dateUtils.js';
import { useMainStore } from '../store';

const router = useRouter();
const store = useMainStore();

const loading = ref(true);
const alertItems = ref([]);
const mergeCandidates = ref([]);

const noPlanningPeriodCount = computed(() => alertItems.value.filter(a => a.issue === "noPlanningPeriod").length);

onMounted(() => {
    loadAlerts();
});

function loadAlerts() {
    loading.value = true;

    Promise.all([
      axios.get(`${process.env.VUE_APP_API_URL}/Appointments/getFutureOutsideOpenings`, { headers: authHeader() }),
      axios.get(`${process.env.VUE_APP_API_URL}/Clients/withCounts`, { headers: authHeader() })
    ])
      .then(([alertsResponse, clientsResponse]) => {
          loading.value = false;
          alertItems.value = (alertsResponse.data || []).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
          mergeCandidates.value = buildMergeCandidates(clientsResponse.data || []);

          store.$patch((state) => {
            state.alertCount = alertItems.value.length + mergeCandidates.value.length;
          });
      })
      .catch(reason => {
          loading.value = false;
          console.log(reason);
          alert("Erreur lors du chargement des alertes.");
      });
}

function issueLabel(issue) {
    if (issue === "noPlanningPeriod") return "Aucune période active";
    return "Hors ouverture";
}

function mergeReasonLabel(reason) {
    if (reason === "samePhone") return "Même téléphone";
    return "Même nom";
}

function buildMergeCandidates(clients) {
    const byPhone = new Map();
    const byName = new Map();
    const suggestions = [];
    const seenPairs = new Set();

    clients.forEach((client) => {
        const normalizedPhone = normalizePhone(client.phone);
        const normalizedName = normalizeText(client.name);

        if (normalizedPhone.length >= 8) {
            if (!byPhone.has(normalizedPhone)) byPhone.set(normalizedPhone, []);
            byPhone.get(normalizedPhone).push(client);
        }

        if (normalizedName.length > 0) {
            if (!byName.has(normalizedName)) byName.set(normalizedName, []);
            byName.get(normalizedName).push(client);
        }
    });

    addGroupSuggestions(byPhone, "samePhone", suggestions, seenPairs);
    addGroupSuggestions(byName, "sameName", suggestions, seenPairs);

    return suggestions;
}

function addGroupSuggestions(groupMap, reason, suggestions, seenPairs) {
    groupMap.forEach((group) => {
        if (!group || group.length < 2) return;

        for (let i = 0; i < group.length; i++) {
            for (let j = i + 1; j < group.length; j++) {
                const a = group[i];
                const b = group[j];
                const leftId = Number(a.clientid);
                const rightId = Number(b.clientid);
                const pairKey = leftId < rightId ? `${leftId}-${rightId}` : `${rightId}-${leftId}`;

                if (seenPairs.has(`${pairKey}:${reason}`)) continue;
                seenPairs.add(`${pairKey}:${reason}`);

                suggestions.push({
                    reason,
                    leftClientId: leftId,
                    rightClientId: rightId,
                    leftName: a.name,
                    rightName: b.name,
                    leftPhone: a.phone,
                    rightPhone: b.phone,
                    leftAddress: a.address || '',
                    rightAddress: b.address || '',
                    leftComment: a.comment || '',
                    rightComment: b.comment || '',
                    leftPast: Number(a.pastAppointments || 0),
                    rightPast: Number(b.pastAppointments || 0),
                    leftFuture: Number(a.futureAppointments || 0),
                    rightFuture: Number(b.futureAppointments || 0),
                    _keepChoice: null,
                    _merging: false,
                    _merged: false
                });
            }
        }
    });
}

function normalizeText(text) {
    return (text || "").toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "").trim();
}

function normalizePhone(text) {
    return (text || "").replace(/\D/g, "");
}

function executeMerge(pair, side) {
    if (pair._merging || pair._merged) return;

    const keepId = side === 'left' ? pair.leftClientId : pair.rightClientId;
    const mergeId = side === 'left' ? pair.rightClientId : pair.leftClientId;
    const keepName = side === 'left' ? pair.leftName : pair.rightName;
    const mergeName = side === 'left' ? pair.rightName : pair.leftName;

    if (!confirm(`Fusionner "${mergeName}" dans "${keepName}" ?\nLe dossier "${mergeName}" sera supprimé.`)) {
      return;
    }

    pair._keepChoice = side;
    pair._merging = true;

    axios
      .post(`${process.env.VUE_APP_API_URL}/Clients/merge?mergeClientId=${mergeId}&keepClientId=${keepId}`, JSON.stringify({}), { headers: authHeader() })
      .then(() => {
          pair._merging = false;
          pair._merged = true;
          setTimeout(() => {
            mergeCandidates.value = mergeCandidates.value.filter(p => p !== pair);
            store.$patch((state) => {
              state.alertCount = alertItems.value.length + mergeCandidates.value.length;
            });
          }, 1200);
      })
      .catch((reason) => {
          pair._merging = false;
          pair._keepChoice = null;
          console.log(reason);
          alert("Erreur lors de la fusion.");
      });
}

function goToAgenda(date) {
    store.$patch((state) => { state.loadDate = date; });
    router.push('/agenda');
}

function goToClient(id) {
    store.$patch((state) => { state.currentClientFile = id; });
    router.push('/clients');
}
</script>
